import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "sonner";
import meetingService from "../../services/meetingService";
import { analystService } from "../../services/analystService";
import { researchService } from "../../services/researchService";
import { SendMessage } from "./SendMessage";
import { InviteOthers } from "./InviteOthers";
import { ScheduleMeetingDialog } from "../ScheduleMeetingDialog";
import type { ProfessionalProfile } from "../ScheduleMeetingDialog";
import type { ResearchReport } from "../../interface/ResearchReport";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { TrendingUp, TrendingDown, FileText, Users, Search, Filter, Target, Award, Clock, Star, Eye, MessageSquare, Briefcase, User, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { MEETING_TYPES } from "../../utils/financialTaxonomy";
import { Progress } from "../ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface ClientPortfolioSummary {
  clientId: string;
  clientName: string;
  email?: string;
  role: "investor" | "advisor";
  portfolioValue: number;
  totalReturn: number;
  totalPercentageReturn: number;
  ytdReturn: number;
  riskScore: number;
  lastRebalance: string;
  status: "On Track" | "Review Needed" | "Attention Required" | "Active" | "Review";
  holdings: number;
  researchAdopted?: string | null;
  focusAreas?: string[];
}


interface StockRecommendation {
  symbol: string;
  name: string;
  sector: string;
  currentPrice: number;
  targetPrice: number;
  rating: string;
  potentialReturn: number;
}

const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case "investor":
      return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
    case "advisor":
      return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
    default:
      return "";
  }
};

type ClientView = "all" | "investor" | "advisor" | "INVESTOR" | "ADVISOR";

interface AnalystPortfolioProps {
  userData?: any;
}

export function AnalystPortfolio({ userData }: AnalystPortfolioProps = {}) {
  const { userId, user } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<{investor: boolean; advisor: boolean}>({
    investor: true,
    advisor: true,
  });
  const [clientView, setClientView] = useState<ClientView>("all");
  const [showAllReports, setShowAllReports] = useState(false);
  const [messageClient, setMessageClient] = useState<ClientPortfolioSummary | null>(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const [meetings, setMeetings] = useState<any[]>([]);
  const [clientPortfolios, setClientPortfolios] = useState<ClientPortfolioSummary[]>([]);
  const [recommendations, setRecommendations] = useState<StockRecommendation[]>([]);
  const [researchNotes, setResearchNotes] = useState<ResearchReport[]>([]);
  const [researchProductivityData, setResearchProductivityData] = useState<any[]>([]);
  const [marketTrends, setMarketTrends] = useState<any[]>([]);
  const [researchPerformance, setResearchPerformance] = useState<any[]>([]);

  const [yearsExperience, setYearsExperience] = useState(userData?.yearsExperience || "3-5");
  const [certifications, setCertifications] = useState<string[]>(userData?.certifications || []);
  const [rating, setRating] = useState(userData?.rating || 4.5);

  const [isClientsLoading, setIsClientsLoading] = useState(true);
  const [isReportsLoading, setIsReportsLoading] = useState(false);
  const [isRecommendationsLoading, setIsRecommendationsLoading] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    const fetchClients = async () => {
      setIsClientsLoading(true);
      setClientsError(null);
      try {
        const response = await analystService.getClients(userId);
        const raw = Array.isArray(response) ? response : (response?.clients || response?.data || []);
        const mapped: ClientPortfolioSummary[] = raw.map((c: any, index: number) => ({
          clientId: c.clientId || c.id || `client-${index + 1}`,
          clientName: c.clientName || c.name || `Client ${index + 1}`,
          email: c.email || "",
          role: (c.role || c.clientRole || "investor") as "investor" | "advisor",
          portfolioValue: c.portfolioValue || c.totalValue || c.aum || 0,
          totalReturn: c.totalReturn || c.return || 0,
          totalPercentageReturn: c.totalPercentageReturn || 0,
          ytdReturn: c.ytdReturn || c.return || 0,
          riskScore: c.riskScore || c.risk || 5,
          lastRebalance: c.lastRebalance || c.lastRebalanced || "",
          status: c.status || c.clientStatus || "Active",
          holdings: c.holdings || c.positions || 0,
          researchAdopted: c.researchAdopted ?? null,
          focusAreas: c.focusAreas || c.specialties || [],
        }));
        setClientPortfolios(mapped);
      } catch (error: any) {
        console.error("Failed to fetch clients:", error);
        setClientsError(error.message || "Failed to load client data");
      } finally {
        setIsClientsLoading(false);
      }
    };

    const fetchResearchReports = async () => {
      setIsReportsLoading(true);
      try {
        const response = await researchService.getResearchReports({ requesterId: userId, requesterRole: 'analyst', size: 10,status: 'published' });
        const raw = Array.isArray(response) ? response : (response?.content || response?.data || []);
        const mapped: ResearchReport[] = raw.map((r: any) => ({
          id: r.id || r._id || "",
          title: r.title || "",
          reportType: r.reportType || r.type || "Market Analysis",
          summary: r.summary || "",
          content: r.content || "",
          author: r.author || { id: "", role: "", name: "", firm: "" },
          publishedDate: r.publishedDate || r.date || r.createdAt || "",
          distributionType: r.distributionType || "public",
          priority: r.priority || "medium",
          status: r.status || "Draft",
          tags: r.tags || [],
          securities: r.securities || [],
          sectors: r.sectors || [],
          views: r.views ?? 0,
          recipients: r.recipients || [],
          documents: r.documents || [],
          readByCurrentUser: r.readByCurrentUser ?? false,
        }));
        setResearchNotes(mapped);
      } catch (error: any) {
        console.error("Failed to fetch research reports:", error);
      } finally {
        setIsReportsLoading(false);
      }
    };

    const fetchRecommendations = async () => {
      setIsRecommendationsLoading(true);
      try {
        const response = await analystService.getRecommendations({});
        const raw = Array.isArray(response) ? response : (response?.recommendations || response?.data || []);
        const mapped: StockRecommendation[] = raw.map((r: any) => ({
          symbol: r.symbol || r.ticker || "",
          name: r.name || r.companyName || "",
          sector: r.sector || "",
          currentPrice: r.currentPrice || r.price || 0,
          targetPrice: r.targetPrice || r.target || 0,
          rating: r.rating || "Hold",
          potentialReturn: r.potentialReturn ?? (
            r.targetPrice && r.currentPrice
              ? ((r.targetPrice - r.currentPrice) / r.currentPrice * 100)
              : 0
          ),
        }));
        setRecommendations(mapped);
      } catch (error: any) {
        console.error("Failed to fetch recommendations:", error);
      } finally {
        setIsRecommendationsLoading(false);
      }
    };

    const fetchDashboard = async () => {
      try {
        const response = await analystService.getDashboard();
        const d = response?.data || response || {};
        if (d.researchProductivity) setResearchProductivityData(d.researchProductivity);
        if (d.sectorPerformance || d.marketTrends) setMarketTrends(d.sectorPerformance || d.marketTrends);
        if (d.performanceMetrics) setResearchPerformance(d.performanceMetrics);
      } catch (error: any) {
        console.error("Failed to fetch dashboard data:", error);
      }
    };

    const fetchProfile = async () => {
      try {
        const response = await analystService.getProfile();
        const p = response?.data || response || {};
        if (p.yearsExperience) setYearsExperience(p.yearsExperience);
        if (p.certifications) setCertifications(p.certifications);
        if (p.rating) setRating(p.rating);
      } catch (error: any) {
        console.error("Failed to fetch profile:", error);
      }
    };

    fetchClients();
    fetchResearchReports();
    //fetchRecommendations();
    //fetchDashboard();
    //fetchProfile();
  }, [userId]);

  const fetchMeetings = async () => {
    if (!userId) return;
    try {
      const [pendingRes, acceptedRes] = await Promise.all([
        meetingService.getMyMeetings(userId, "PENDING", false, ''),
        meetingService.getMyMeetings(userId, "ACCEPTED", false, '')
      ]);
      const pMeetings = pendingRes.data || (Array.isArray(pendingRes) ? pendingRes : []);
      const aMeetings = acceptedRes.data || (Array.isArray(acceptedRes) ? acceptedRes : []);
      const allMeetings = [...pMeetings, ...aMeetings].sort((a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      setMeetings(allMeetings);
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
    }
  };

  const handleAcceptMeeting = async (meetingId) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "ACCEPTED");
      toast.success("Meeting accepted");
      fetchMeetings();
    } catch (e) { toast.error(e.message || "Failed to accept"); }
  };

  const handleDeclineMeeting = async (meetingId) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "DECLINED");
      toast.success("Meeting declined");
      fetchMeetings();
    } catch (e) { toast.error(e.message || "Failed to decline"); }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "CANCELLED");
      toast.success("Meeting cancelled");
      fetchMeetings();
    } catch (e) { toast.error(e.message || "Failed to cancel"); }
  };

  useEffect(() => {
    if (userId) fetchMeetings();
  }, [userId]);

  const investorClients = clientPortfolios.filter(c => c.role === "investor");
  const advisorClients = clientPortfolios.filter(c => c.role === "advisor");
  
  const visibleClients = clientView === "all" ? clientPortfolios
    : clientView === "investor" ? investorClients
    : advisorClients;

  const filteredPortfolios = visibleClients.filter((portfolio) => {
    const matchesSearch = portfolio.clientName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter[portfolio.role];
    return matchesSearch && matchesRole;
  });

  const totalAUM = filteredPortfolios.reduce((sum, p) => sum + p.portfolioValue, 0);
  const avgReturn = filteredPortfolios.length > 0
    ? filteredPortfolios.reduce((sum, p) => sum + p.totalPercentageReturn, 0) / filteredPortfolios.length
    : 0;

  const avgInvestorReturn = investorClients.length > 0
    ? (investorClients.reduce((sum, c) => sum + c.totalPercentageReturn, 0) / investorClients.length).toFixed(1)
    : "0.0";

  const displayedAvgReturn = clientView === "investor" ? Number(avgInvestorReturn) : avgReturn;
  const displayedAvgReturnClass =
    displayedAvgReturn > 0 ? "text-green-600" : displayedAvgReturn < 0 ? "text-red-600" : "text-muted-foreground";

  const advisorsWithAdoption = advisorClients.filter(c => c.researchAdopted);
  const avgAdvisorAdoption = advisorsWithAdoption.length > 0
    ? advisorsWithAdoption.reduce((sum, c) => sum + parseFloat(c.researchAdopted!.replace("%", "")), 0) / advisorsWithAdoption.length
    : 0;

  const researchReviewedCount = researchNotes.filter(report =>
    report.recipients?.some(r =>
      r.role?.toLowerCase() === 'advisor' && r.readAt != null && r.readAt.trim() !== ''
    )
  ).length;

  const visibleReports = clientView === "all" ? researchNotes
    : researchNotes.filter(r => {
        const distType = r.distributionType?.toLowerCase();
        return distType === "public" ||
          distType === "broadcast" ||
          (distType === "individual" && r.recipients?.some(rec => rec.role?.toLowerCase() === clientView));
      });

  const investorCount = clientPortfolios.filter(p => p.role === "investor").length;
  const advisorCount = clientPortfolios.filter(p => p.role === "advisor").length;

  return (
    <div className="space-y-6">
      {isClientsLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading portfolio...
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl">Analyst Dashboard</h2>
          <p className="text-muted-foreground">
            Track your research performance and client portfolio insights
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={selectedClientId} onValueChange={setSelectedClientId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select a client" />
            </SelectTrigger>
            <SelectContent>
              {clientPortfolios.map((cp) => (
                <SelectItem key={cp.clientId} value={cp.clientId}>
                  {cp.clientName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            disabled={!selectedClientId}
            onClick={() => setIsScheduleMeetingOpen(true)}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            Schedule Meeting
          </Button>
          <Button onClick={() => setIsInviteOpen(true)}>
            <Users className="mr-2 h-4 w-4" />
            Invite New Client
          </Button>
        </div>
      </div>

      {/* Client View Toggle */}
      <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
        <button
          onClick={() => setClientView("all")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            clientView === "all"
              ? "bg-white dark:bg-background shadow-sm text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Users className="h-4 w-4" />
          All Clients
          <span className="ml-1 text-xs bg-muted-foreground/20 rounded-full px-1.5 py-0.5">
            {clientPortfolios.length}
          </span>
        </button>
        <button
          onClick={() => setClientView("investor")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            clientView === "investor"
              ? "bg-blue-600 shadow-sm text-white"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <User className="h-4 w-4" />
          Investors
          <span className="ml-1 text-xs bg-white/20 rounded-full px-1.5 py-0.5">
            {investorClients.length}
          </span>
        </button>
        <button
          onClick={() => setClientView("advisor")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            clientView === "advisor"
              ? "bg-green-600 shadow-sm text-white"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Briefcase className="h-4 w-4" />
          Advisors
          <span className="ml-1 text-xs bg-white/20 rounded-full px-1.5 py-0.5">
            {advisorClients.length}
          </span>
        </button>
      </div>


      {/* Loading State */}
      {isClientsLoading && clientPortfolios.length === 0 && (
        <Card>
          <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading client data...
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {clientsError && clientPortfolios.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-red-500 mb-2">Failed to load data</p>
            <p className="text-sm text-muted-foreground">{clientsError}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isClientsLoading && !clientsError && clientPortfolios.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No clients found</p>
            <p className="text-sm">Connect with investors or advisors to get started</p>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {!isClientsLoading && !clientsError && clientPortfolios.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {clientView === "all" ? "Active Clients" :
                 clientView === "investor" ? "Investor Clients" : "Advisor Clients"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl">{visibleClients.length}</div>
              <p className="text-xs text-muted-foreground">
                {clientView === "all"
                  ? `${investorClients.length} investors \u2022 ${advisorClients.length} advisors`
                  : clientView === "investor"
                  ? "Direct portfolio clients"
                  : "Advisory practice clients"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {clientView === "advisor" ? "Research Reviewed" : "Avg Client Return"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clientView === "advisor" ? (
                <>
                  <div className="text-2xl text-green-600">
                    {researchReviewedCount > 0 ? researchReviewedCount : "0"}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {researchReviewedCount === 1 ? "report reviewed by advisors" : "reports reviewed by advisors"}
                  </p>
                </>
              ) : (
                <>
                  <div className={`text-2xl ${displayedAvgReturnClass}`}>
                    {displayedAvgReturn > 0 ? "+" : ""}{displayedAvgReturn.toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {clientView === "investor" 
                      ? `Total Returns across ${investorClients.length} investor clients`
                      : "Total Return performance"}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Reports Published</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl">
                {isReportsLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  visibleReports.length
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {visibleReports.length} total recent research
              </p>
              <p className="text-xs text-muted-foreground">
                {clientView === "all" ? "This quarter (Target: 12)"
                  : clientView === "investor" ? "Investor-targeted reports"
                  : "Advisor-targeted reports"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Analyst Rating</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl flex items-center gap-1">
                {rating}
                <Star className="h-5 w-5 fill-amber-500 text-amber-500" />
              </div>
              <p className="text-xs text-muted-foreground">
                {clientView === "advisor" ? "From advisor reviews" :
                 clientView === "investor" ? "From investor reviews" :
                 "Based on client reviews"}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Metrics */}
      {researchPerformance.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Your Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {researchPerformance.map((metric: any) => (
                <div key={metric.metric || metric.name} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{metric.metric || metric.name}</span>
                    <span className="text-muted-foreground">
                      {metric.value} / {metric.target}
                    </span>
                  </div>
                  <Progress value={(metric.value / metric.target) * 100} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Research Productivity */}
        {researchProductivityData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Research Productivity</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={researchProductivityData}>
                  <CartesianGrid key="grid" strokeDasharray="3 3" />
                  <XAxis key="x-axis" dataKey="month" />
                  <YAxis key="y-axis" />
                  <Tooltip key="tooltip" />
                  <Legend key="legend" />
                  <Line
                    key="reports"
                    type="monotone"
                    dataKey="reports"
                    stroke="#3b82f6"
                    name="Reports Published"
                  />
                  <Line
                    key="coverage"
                    type="monotone"
                    dataKey="coverage"
                    stroke="#10b981"
                    name="Stock Coverage"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Sector Performance Tracking */}
        {marketTrends.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Sector Performance Tracking</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={marketTrends}>
                  <CartesianGrid key="grid" strokeDasharray="3 3" />
                  <XAxis key="x-axis" dataKey="month" />
                  <YAxis key="y-axis" />
                  <Tooltip key="tooltip" />
                  <Legend key="legend" />
                  {marketTrends[0] && Object.keys(marketTrends[0])
                    .filter(k => k !== "month")
                    .map((key) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={
                          key === "tech" || key === "Technology" ? "#3b82f6"
                          : key === "healthcare" || key === "Healthcare" ? "#8b5cf6"
                          : key === "finance" || key === "Finance" ? "#10b981"
                          : "#f59e0b"
                        }
                        name={key.charAt(0).toUpperCase() + key.slice(1)}
                        strokeWidth={2}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Research & Active Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Research */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Recent Research
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAllReports(v => !v)}>
                {showAllReports ? "Show Less" : "View All"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isReportsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : visibleReports.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No research reports yet</p>
            ) : (
              <div className="space-y-4">
                {(showAllReports ? visibleReports : visibleReports.slice(0, 5)).map((report, idx) => (
                  <div key={report.id || idx} className="flex items-start justify-between pb-4 border-b last:border-0 last:pb-0">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium">{report.title}</span>
                        <Badge variant={report.status === "Published" ? "default" : "secondary"}>
                          {report.status}
                        </Badge>
                        {clientView === "all" && report.distributionType && (
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              report.distributionType === "individual"
                                ? report.recipients?.every(r => r.role === "investor")
                                  ? "border-blue-300 text-blue-600 dark:text-blue-400"
                                  : report.recipients?.every(r => r.role === "advisor")
                                  ? "border-green-300 text-green-600 dark:text-green-400"
                                  : "border-muted-foreground/30 text-muted-foreground"
                                : "border-muted-foreground/30 text-muted-foreground"
                            }`}
                          >
                            {report.distributionType === "public" || report.distributionType === "broadcast"
                              ? "All clients"
                              : report.distributionType === "individual"
                              ? [...new Set(report.recipients?.map(r => r.role?.toLowerCase()).filter(Boolean) || [])]
                                  .map(role => role.charAt(0).toUpperCase() + role.slice(1))
                                  .sort()
                                  .join(", ") || "Individual"
                              : report.distributionType}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{report.publishedDate}</span>
                        {report.status === "Published" && report.views != null && (
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {report.views.toLocaleString()} views
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Client Portfolios */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {clientView === "advisor" ? "Advisor Client Portfolios" :
                 clientView === "investor" ? "Investor Portfolios" :
                 "Active Client Portfolios"}
              </CardTitle>
              <Button variant="ghost" size="sm">View All</Button>
            </div>
          </CardHeader>
          <CardContent>
            {isClientsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : visibleClients.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No clients found</p>
            ) : (
              <div className="space-y-4">
                {visibleClients.slice(0, 4).map((client, idx) => (
                  <div key={idx} className="flex items-center justify-between pb-4 border-b last:border-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-sm">{client.clientName}</span>
                        {clientView === "all" && (
                          <Badge
                            variant="secondary"
                            className={getRoleBadgeColor(client.role)}
                          >
                            {client.role === "investor" ? "Investor" : "Advisor"}
                          </Badge>
                        )}
                        <Badge variant={client.status === "Review" || client.status === "Review Needed" ? "outline" : "secondary"}>
                          {client.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {client.role === "advisor" ? "AUM: " : "Portfolio: "}
                        ${(client.portfolioValue / 1000).toFixed(0)}K
                      </p>
                      {client.focusAreas && client.focusAreas.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {client.focusAreas.map(area => (
                            <span key={area} className="text-xs px-1.5 py-0.5 bg-muted rounded-full text-muted-foreground">
                              {area}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      {client.role === "investor" ? (
                        <>
                          <div className={`${client.totalPercentageReturn > 0 ? "text-green-600" : client.totalPercentageReturn < 0 ? "text-red-600" : "text-muted-foreground"} text-sm font-medium`}>
                            {client.totalPercentageReturn > 0 ? "+" : ""}{client.totalPercentageReturn.toFixed(2)}%
                          </div>
                          <div className="text-xs text-muted-foreground">Portfolio return</div>
                        </>
                      ) : (
                        <>
                          <div className="text-green-600 text-sm font-medium">{client.researchAdopted || "N/A"}</div>
                          <div className="text-xs text-muted-foreground">Adoption rate</div>
                        </>
                      )}
                      <Button variant="ghost" size="sm" className="h-6 text-xs mt-1" onClick={() => setMessageClient(client)}>
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Message
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Meetings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Upcoming Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {meetings.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                <p>No upcoming meetings</p>
              </div>
            ) : (
              meetings.map((meeting) => {
                const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+')
                  ? meeting.startTime
                  : `${meeting.startTime}Z`;
                const formattedTime = new Date(dateStr).toLocaleString([], {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                });
                const clientName = meeting.hostId === userId
                  ? clientPortfolios.find(c => c.clientId === meeting.participantId)?.clientName || meeting.participantId
                  : meeting.hostFullName || meeting.hostId;
                return (
                  <div key={meeting.meetingId || meeting.id} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{clientName}</div>
                        <div className="text-xs text-muted-foreground">{MEETING_TYPES.find(mt => mt.value === meeting.meetingType?.toLowerCase())?.label || meeting.title || "Meeting"}</div>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <div className="text-sm">{formattedTime}</div>
                        <Badge variant={meeting.status === "ACCEPTED" ? "default" : "secondary"} className="text-xs mt-1">
                          {meeting.status === "ACCEPTED" ? "Accepted" : meeting.status === "PENDING" ? "Pending" : meeting.status}
                        </Badge>
                      </div>
                      {meeting.status === "PENDING" && meeting.hostId !== userId && (
                        <div className="flex flex-col gap-1">
                          <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => handleAcceptMeeting(meeting.meetingId || meeting.id)}>Accept</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleDeclineMeeting(meeting.meetingId || meeting.id)}>Decline</Button>
                        </div>
                      )}
                      {meeting.status === "ACCEPTED" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleCancelMeeting(meeting.meetingId || meeting.id)}>Cancel</Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="clients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="clients">Client Portfolios</TabsTrigger>

          <TabsTrigger value="research">Research Notes</TabsTrigger>
        </TabsList>

        {/* Client Portfolios Tab */}
        <TabsContent value="clients" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Client Portfolio Overview</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search clients..." 
                      className="pl-8 w-64"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Filter className="h-4 w-4 mr-2" />
                        Filter by Role
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel>Client Type</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        checked={roleFilter.investor}
                        onCheckedChange={(checked) => 
                          setRoleFilter({...roleFilter, investor: checked})
                        }
                      >
                        <Badge variant="secondary" className="mr-2 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                          Investor
                        </Badge>
                        ({investorCount})
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={roleFilter.advisor}
                        onCheckedChange={(checked) => 
                          setRoleFilter({...roleFilter, advisor: checked})
                        }
                      >
                        <Badge variant="secondary" className="mr-2 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                          Advisor
                        </Badge>
                        ({advisorCount})
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isClientsLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading clients...
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">{clientView === "advisor" ? "AUM" : "Portfolio Value"}</TableHead>
                    <TableHead className="text-right">Total Return</TableHead>
                    <TableHead className="text-right">Risk Score</TableHead>
                    <TableHead className="text-right">Holdings</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPortfolios.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No clients found matching your criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPortfolios.map((portfolio, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {portfolio.clientName.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{portfolio.clientName}</div>
                            <div className="text-xs text-muted-foreground">
                              Last rebalance: {portfolio.lastRebalance || "N/A"}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={getRoleBadgeColor(portfolio.role)}>
                          {portfolio.role === "investor" ? "Investor" : "Advisor"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {clientView === "advisor"
                          ? `$${(portfolio.portfolioValue / 1000000).toFixed(2)}M`
                          : `$${portfolio.portfolioValue.toLocaleString()}`
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={`inline-flex items-center gap-1 ${
                            portfolio.totalPercentageReturn >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {portfolio.totalPercentageReturn >= 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          {portfolio.totalPercentageReturn >= 0 ? "+" : ""}
                          {portfolio.totalPercentageReturn.toFixed(2)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {portfolio.riskScore.toFixed(1)}/10
                      </TableCell>
                      <TableCell className="text-right">{portfolio.holdings}</TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            portfolio.status === "On Track" || portfolio.status === "Active"
                              ? "secondary"
                              : portfolio.status === "Review Needed" || portfolio.status === "Review"
                              ? "outline"
                              : "destructive"
                          }
                        >
                          {portfolio.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Current Stock Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              {isRecommendationsLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading recommendations...
                </div>
              ) : recommendations.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No recommendations yet</p>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead className="text-right">Current Price</TableHead>
                    <TableHead className="text-right">Target Price</TableHead>
                    <TableHead className="text-right">Potential Return</TableHead>
                    <TableHead className="text-center">Rating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recommendations.map((rec) => (
                    <TableRow key={rec.symbol}>
                      <TableCell className="font-medium">{rec.symbol}</TableCell>
                      <TableCell>{rec.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{rec.sector}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        ${rec.currentPrice.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        ${rec.targetPrice.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={
                            rec.potentialReturn >= 0 ? "text-green-600" : "text-red-600"
                          }
                        >
                          {rec.potentialReturn >= 0 ? "+" : ""}
                          {rec.potentialReturn.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            rec.rating === "Strong Buy"
                              ? "default"
                              : rec.rating === "Buy"
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {rec.rating}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Research Notes Tab */}
        <TabsContent value="research" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Research & Reports</CardTitle>
                <Button variant="outline" size="sm">
                  <FileText className="h-4 w-4 mr-2" />
                  New Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isReportsLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading reports...
                </div>
              ) : researchNotes.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No research reports yet</p>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {researchNotes.map((note) => (
                    <TableRow key={note.id}>
                      <TableCell className="font-medium">{note.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{note.reportType}</Badge>
                      </TableCell>
                      <TableCell>{note.publishedDate}</TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            note.status === "Published"
                              ? "default"
                              : note.status === "Draft"
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {note.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          {note.status === "Published" ? "View" : "Edit"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <ScheduleMeetingDialog
        isOpen={isScheduleMeetingOpen}
        onOpenChange={setIsScheduleMeetingOpen}
        professional={(() => {
          const cp = clientPortfolios.find(c => c.clientId === selectedClientId);
          return cp ? { id: cp.clientId, name: cp.clientName } : null;
        })()}
        onScheduleSuccess={() => {
          fetchMeetings();
        }}
      />
      <InviteOthers
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        sender={user?.fullName || user?.name || userId || "Analyst"}
      />
      <SendMessage
        open={!!messageClient}
        clientName={messageClient?.clientName}
        clientEmail={messageClient?.email || ""}
        senderName={user?.fullName || user?.name || userId || "Analyst"}
        onClose={() => setMessageClient(null)}
      />
    </div>
  );
}
